import { useCallback, useEffect, useMemo, useState } from "react";
import type { EventAttendee } from "@tih/shared";
import {
  ApiError,
  adminBulkCreateEventAttendees,
  adminCheckInAttendee,
  adminCheckOutAttendee,
  adminCreateEventAttendee,
  adminDeleteEventAttendee,
  adminFetchEventAttendees,
} from "../../../api/client";
import { useEventHub } from "../../../context/EventHubContext";
import { formatCheckInTime, parseBulkAttendeeLines } from "../../../lib/attendeeBulkParse";

type Panel = "checkin" | "add" | "bulk";

export function EventAttendeesPanel() {
  const hub = useEventHub();
  const eventId = hub.eventId;

  const [attendees, setAttendees] = useState<EventAttendee[]>([]);
  const [search, setSearch] = useState("");
  const [panel, setPanel] = useState<Panel>("checkin");
  const [listLoading, setListLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [addName, setAddName] = useState("");
  const [addMobile, setAddMobile] = useState("");
  const [addLocation, setAddLocation] = useState("");
  const [addEmail, setAddEmail] = useState("");
  const [addCheckIn, setAddCheckIn] = useState(true);
  const [bulkText, setBulkText] = useState("");
  const [bulkCheckIn, setBulkCheckIn] = useState(false);

  const loadAttendees = useCallback(async () => {
    setListLoading(true);
    try {
      const rows = await adminFetchEventAttendees(eventId);
      setAttendees(rows);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load attendees");
    } finally {
      setListLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    void loadAttendees();
  }, [loadAttendees]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return attendees;
    return attendees.filter((a) => {
      const hay = [a.name, a.mobile, a.location, a.email]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [attendees, search]);

  const checkedIn = attendees.filter((a) => a.checkedInAt).length;

  async function checkIn(id: number) {
    setBusyId(id);
    setError(null);
    try {
      await adminCheckInAttendee(id);
      setSuccess("Checked in.");
      setSearch("");
      await loadAttendees();
      await hub.refreshSummary();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  async function checkOut(id: number) {
    setBusyId(id);
    try {
      await adminCheckOutAttendee(id);
      await loadAttendees();
      await hub.refreshSummary();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  async function removeAttendee(id: number) {
    if (!confirm("Remove this attendee from the list?")) return;
    await adminDeleteEventAttendee(id);
    await loadAttendees();
    await hub.refreshSummary();
  }

  async function submitAdd() {
    if (!addName.trim()) return;
    setError(null);
    try {
      await adminCreateEventAttendee(eventId, {
        name: addName.trim(),
        mobile: addMobile.trim() || null,
        location: addLocation.trim() || null,
        email: addEmail.trim() || null,
        checkIn: addCheckIn,
      });
      setSuccess(addCheckIn ? `${addName.trim()} added and checked in.` : `${addName.trim()} added.`);
      setAddName("");
      setAddMobile("");
      setAddLocation("");
      setAddEmail("");
      setPanel("checkin");
      setSearch("");
      await loadAttendees();
      await hub.refreshSummary();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }

  async function submitBulk() {
    const rows = parseBulkAttendeeLines(bulkText);
    if (rows.length === 0) {
      setError("Paste at least one line with a name.");
      return;
    }
    setError(null);
    try {
      await adminBulkCreateEventAttendees(eventId, {
        attendees: rows,
        checkInAll: bulkCheckIn,
      });
      setSuccess(`Added ${rows.length} attendee${rows.length === 1 ? "" : "s"}.`);
      setBulkText("");
      setPanel("checkin");
      await loadAttendees();
      await hub.refreshSummary();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }

  async function addAndCheckInFromSearch() {
    const name = search.trim();
    if (!name) return;
    setError(null);
    try {
      await adminCreateEventAttendee(eventId, { name, checkIn: true });
      setSuccess(`${name} registered and checked in.`);
      setSearch("");
      await loadAttendees();
      await hub.refreshSummary();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }

  return (
    <div className="admin-checkin-page">
      <p className="admin-muted admin-checkin-stats" style={{ marginBottom: 16 }}>
        {checkedIn} checked in · {attendees.length} registered
      </p>

      {error && <div className="form-error admin-banner">{error}</div>}
      {success && (
        <div className="form-success admin-banner" onAnimationEnd={() => setSuccess(null)}>
          {success}
        </div>
      )}

      <div className="admin-tab-row" style={{ marginBottom: 16 }}>
        {(
          [
            ["checkin", "Check-in"],
            ["add", "Add one"],
            ["bulk", "Bulk add"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`admin-tab${panel === key ? " is-active" : ""}`}
            onClick={() => setPanel(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {panel === "checkin" && (
        <>
          <div className="admin-checkin-search-wrap">
            <input
              type="search"
              className="admin-checkin-search"
              placeholder="Search name, phone, email, location…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoComplete="off"
            />
          </div>

          {search.trim() && filtered.length === 0 && !listLoading && (
            <div className="admin-checkin-walkin">
              <p>
                No match for <strong>{search.trim()}</strong>.
              </p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void addAndCheckInFromSearch()}
              >
                Add &amp; check in
              </button>
            </div>
          )}

          {listLoading ? (
            <p>Loading attendees…</p>
          ) : (
            <ul className="admin-checkin-list">
              {filtered.map((a) => (
                <li
                  key={a.id}
                  className={`admin-checkin-row${a.checkedInAt ? " is-checked-in" : ""}`}
                >
                  <div className="admin-checkin-row-main">
                    <strong>{a.name}</strong>
                    <span className="admin-checkin-row-meta">
                      {[a.mobile, a.location, a.email].filter(Boolean).join(" · ") || "—"}
                    </span>
                    {a.checkedInAt && (
                      <span className="admin-checkin-time">
                        In at {formatCheckInTime(a.checkedInAt)}
                        {a.checkedInByName ? ` · ${a.checkedInByName}` : ""}
                      </span>
                    )}
                  </div>
                  <div className="admin-checkin-row-actions">
                    {a.checkedInAt ? (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        disabled={busyId === a.id}
                        onClick={() => void checkOut(a.id)}
                      >
                        Undo
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={busyId === a.id}
                        onClick={() => void checkIn(a.id)}
                      >
                        Check in
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => void removeAttendee(a.id)}
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {panel === "add" && (
        <div className="admin-card">
          <h3 className="admin-card-title">Add attendee</h3>
          <div className="admin-card-row">
            <label className="admin-field">
              <span>Name *</span>
              <input value={addName} onChange={(e) => setAddName(e.target.value)} />
            </label>
            <label className="admin-field">
              <span>Mobile</span>
              <input value={addMobile} onChange={(e) => setAddMobile(e.target.value)} />
            </label>
            <label className="admin-field">
              <span>Location</span>
              <input value={addLocation} onChange={(e) => setAddLocation(e.target.value)} />
            </label>
            <label className="admin-field">
              <span>Email</span>
              <input type="email" value={addEmail} onChange={(e) => setAddEmail(e.target.value)} />
            </label>
            <label className="admin-field admin-field-check">
              <input
                type="checkbox"
                checked={addCheckIn}
                onChange={(e) => setAddCheckIn(e.target.checked)}
              />
              <span>Check in immediately</span>
            </label>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!addName.trim()}
            onClick={() => void submitAdd()}
          >
            Save attendee
          </button>
        </div>
      )}

      {panel === "bulk" && (
        <div className="admin-card">
          <h3 className="admin-card-title">Bulk add attendees</h3>
          <p className="admin-muted">
            One person per line. Use commas or tabs:{" "}
            <code>name, mobile, location, email</code>
          </p>
          <textarea
            className="admin-checkin-bulk"
            rows={12}
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
          />
          <label className="admin-field admin-field-check">
            <input
              type="checkbox"
              checked={bulkCheckIn}
              onChange={(e) => setBulkCheckIn(e.target.checked)}
            />
            <span>Check in everyone immediately</span>
          </label>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!bulkText.trim()}
            onClick={() => void submitBulk()}
          >
            Import attendees
          </button>
        </div>
      )}
    </div>
  );
}
